import { supabase, isConfigured } from './supabase.js';
import { emit } from '../state/bus.js';

/* Magic link, plus a silent anonymous session underneath everyone who hasn't
   used it.

   Every visitor gets a real, server-tracked identity at boot — an anonymous
   Supabase session if there's no session at all yet — so entitlements
   (credits, the presets unlock) can live in the database rather than in
   localStorage, without a signup form standing in the way of trying the app.
   "Sign in" stops being a gate to get past and becomes an upgrade: it
   converts the SAME anonymous user to a real email-linked one in place
   (supabase.auth.updateUser, not a new signInWithOtp signup), which is what
   keeps a purchased credit balance from being orphaned if a visitor later
   decides to attach an email.

   Passwords were deliberately absent here for a long time, and the reason
   given was a good one: no reset flow, no strength rules, nothing to leak.
   What changed is Motion licensing. A link that has to be found in an inbox
   is a fine way to get into a free tool and a bad one for a customer who is
   paying to use it on a deadline — so email+password now sits ALONGSIDE the
   link, and the link keeps working exactly as it did. Nobody is migrated,
   nothing that worked stops working, and anyone who never sets a password
   never sees one.

   Google OAuth is still about ten lines on top of this if it turns out to
   be wanted. */

let session = null;

export const currentUser = () => session?.user || null;
export const isSignedIn = () => !!session;
export const isAnonymous = () => session?.user?.is_anonymous === true;

/**
 * Read the session once at boot, silently start an anonymous one if there is
 * none at all, and then follow it. Emits 'auth:changed' with the user (or
 * null) whenever it moves, so the UI never has to poll.
 */
/* Whether an error means "this token's user is gone" as opposed to "the
   network is having a moment". Only the first is worth destroying a session
   over: treating a blip as a dead account would sign people out of their own
   work for no reason. Matched on the code where there is one, with a message
   test because that code has moved between versions. */
function userIsGone(error) {
  if (!error) return false;
  return error.code === 'user_not_found'
    || /user from sub claim in jwt does not exist/i.test(error.message || '');
}

/* getSession() reads the token out of localStorage and does NOT ask the server
   whether it is still any good. A session whose user has since been deleted —
   removed from Admin, or the account deleted in another tab — therefore
   restores perfectly happily, and then fails every authenticated call with
   "User from sub claim in JWT does not exist". Including, unhelpfully, the one
   that would let somebody sign in again: creating an account tries to UPGRADE
   the current anonymous session, and there is nothing left to upgrade.

   Checked against the server in the background rather than before boot, so the
   app is not held up by a round trip on every load, and healed by discarding
   the dead token and starting a fresh anonymous session. */
async function healDeadSession() {
  const { error } = await supabase.auth.getUser();
  if (!userIsGone(error)) return;
  console.warn('[illusio] session discarded — its user no longer exists');
  await supabase.auth.signOut({ scope: 'local' }).catch(() => {});
  const { data: anon, error: anonErr } = await supabase.auth.signInAnonymously();
  if (!anonErr) session = anon.session;
  emit('auth:changed', currentUser());
}

export async function initAuth() {
  if (!isConfigured) { emit('auth:changed', null); return null; }

  const { data } = await supabase.auth.getSession();
  session = data.session;
  /* Deliberately not awaited — see healDeadSession. */
  if (session) healDeadSession();

  if (!session) {
    /* Fails closed, not loudly: if anonymous sign-in isn't enabled on the
       project (or the request just fails), the app still boots — it falls
       back to today's fully-local, signed-out behaviour rather than
       breaking on something this quiet. */
    const { data: anon, error } = await supabase.auth.signInAnonymously();
    if (!error) session = anon.session;
  }

  supabase.auth.onAuthStateChange((_event, s) => {
    session = s;
    /* A reset link signs the user in and fires this with PASSWORD_RECOVERY.
       Without somewhere to send that, the link would land them in the app
       with a live session and still no password — the flow would appear to
       work and change nothing. ui/account.js listens and offers the field. */
    if (_event === 'PASSWORD_RECOVERY') emit('auth:recovery', s?.user || null);
    /* A genuinely NEW sign-in, as distinct from a session restored on load
       (INITIAL_SESSION) or a token quietly refreshing. Someone who has just
       arrived from an emailed link is signed in and has no password — this is
       the one moment they can be offered one without it being an interruption,
       and without it they would have to work out that "Forgot your password"
       is how you get a first one. */
    else if (_event === 'SIGNED_IN') emit('auth:signed-in', s?.user || null);
    emit('auth:changed', s?.user || null);
  });

  /* A magic link arrives as a fragment on the URL. Supabase consumes it, but the
     tokens stay visible in the address bar and in history unless they are
     cleared — so clear them. */
  if (location.hash.includes('access_token') || location.search.includes('code=')) {
    /* Everything goes except `next`. Stripping to the bare pathname also threw
       away the intent the link was carrying — which ui/trial.js reads, and
       which exists precisely so a magic link opened in a DIFFERENT browser
       from the one that asked for it still lands on the right form, rather
       than depending on that browser's localStorage. The tokens are in the
       fragment and the code is in the query, so rebuilding the query from
       this one key clears both. */
    const next = new URLSearchParams(location.search).get('next');
    const keep = next ? '?next=' + encodeURIComponent(next) : '';
    history.replaceState(null, '', location.pathname + keep);
  }

  emit('auth:changed', session?.user || null);
  return session;
}

/**
 * Make sure there is a session, starting an anonymous one if the attempt at
 * boot didn't produce one.
 *
 * initAuth() fails silently on purpose so a dead backend can't stop the app
 * from booting, but a feature that genuinely needs a uid (feedback) should
 * ask again at the moment it needs one rather than inherit that silence —
 * the boot attempt may simply have raced a cold network. Resolves to the
 * user or null; the caller decides how loud to be about null.
 */
export async function ensureSession() {
  if (!isConfigured) return null;
  if (session) return session.user;
  const { data, error } = await supabase.auth.signInAnonymously();
  /* A 422 here means anonymous sign-ins are switched off for the project
     (Authentication → Sign In / Providers), not that anything is broken.
     Worth saying out loud, because every uid-backed feature — credits, the
     templates unlock, feedback — quietly does nothing without it. */
  if (error) {
    console.warn('[illusio] no session available:', error.message);
    return null;
  }
  session = data.session;
  return session?.user || null;
}

/**
 * Send the link — or, when the current session is anonymous, upgrade it to a
 * real email IN PLACE (same user id, so credits and presets already tied to
 * this session stay tied to it). Resolves when the mail is away, not when
 * it is clicked.
 */
export async function signIn(email, next) {
  if (!isConfigured) throw new Error('Accounts need Supabase credentials in .env.local.');
  const addr = email.trim();
  /* `next` rides in the link rather than in this browser's storage, because
     the link is very often not opened where it was asked for — signed up on a
     laptop, tapped on a phone; a mail client that opens links in a different
     browser; an incognito window whose storage the normal one cannot see.
     Whatever was being done when the account was needed then survives the
     trip, on any device.

     NOTE: emailRedirectTo must match Supabase's Redirect URLs allow-list. A
     bare origin entry does not necessarily admit a query string, so if links
     ever start failing, that list is the first thing to look at — a broken
     sign-in would be a far worse trade than a missed resume. */
  const back = new URL(location.origin + location.pathname);
  if (next) back.searchParams.set('next', next);
  const opts = { emailRedirectTo: back.toString() };

  /* Plain sign-in when there is nothing to upgrade. */
  if (!isAnonymous()) {
    const { error } = await supabase.auth.signInWithOtp({ email: addr, options: opts });
    if (error) throw error;
    return;
  }

  /* Anonymous: try to convert THIS session in place, so credits and projects
     already earned in this browser stay on the same user id. */
  const { error } = await supabase.auth.updateUser({ email: addr }, opts);
  if (!error) return;

  /* ...unless that address already has an account, which is the common case
     the first version of this got wrong: it surfaced Supabase's "email
     address is already registered" and stopped, leaving someone who simply
     wanted back into their own account staring at an error for having one.

     Anonymous sessions are created silently for every visitor, so almost
     everyone signing in is anonymous at that moment, and anyone returning on
     a new browser hits this every time. Falling back to a normal sign-in is
     what they were asking for: the existing account is the real one, and the
     empty anonymous session this browser happened to mint is worth nothing.

     Matched on the error code where Supabase gives one, with a message test
     as a fallback because that code has been added and renamed across
     versions and a silent miss here would restore the original dead end. */
  /* The session this browser is holding points at a user that no longer
     exists, so there is nothing to convert. Discard it and sign in normally:
     what they asked for was an account, and the dead token is worth nothing.
     Handled here as well as at boot because somebody can reach this form
     before the background check has finished. */
  if (userIsGone(error)) {
    await supabase.auth.signOut({ scope: 'local' }).catch(() => {});
    const { error: freshErr } = await supabase.auth.signInWithOtp({ email: addr, options: opts });
    if (freshErr) throw freshErr;
    return;
  }

  const taken = error.code === 'email_exists'
    || error.code === 'user_already_exists'
    || /already (been )?registered|already exists|already in use/i.test(error.message || '');
  if (!taken) throw error;

  const { error: otpError } = await supabase.auth.signInWithOtp({ email: addr, options: opts });
  if (otpError) throw otpError;
}

/**
 * Sign in with a password. Separate from signIn() above rather than folded
 * into it: that one is a whole negotiation about upgrading an anonymous
 * session in place, and this one deliberately is not.
 *
 * It CANNOT convert an anonymous session the way updateUser({email}) does —
 * signing in as someone else necessarily abandons whatever the anonymous
 * session was holding. That is the right behaviour for its actual use, which
 * is a licensed customer getting back into their own account on a machine
 * that has minted them a throwaway session; it is not a bug to be fixed into
 * an upgrade later.
 */
export async function signInWithPassword(email, password) {
  if (!isConfigured) throw new Error('Accounts need Supabase credentials in .env.local.');
  const { error } = await supabase.auth.signInWithPassword({
    email: email.trim(), password
  });
  if (error) throw error;
}

/**
 * Set (or change) the password on the CURRENT session. Serves both halves of
 * the flow: someone who signed in by link choosing a password for next time,
 * and the last step of a reset, where the recovery link has already
 * established the session this writes to.
 */
export async function setPassword(password) {
  if (!isConfigured) throw new Error('Accounts need Supabase credentials in .env.local.');
  /* The flag rides along with the password itself, in one call, so the two
     cannot disagree — a separate updateUser afterwards could fail and leave
     somebody with a password we then keep offering to set.

     Why a flag at all: nothing on the Supabase user says whether a password
     exists. Both a magic-link user and a password user carry the same email
     identity, so "has one" is not something the client can read — it has to
     be remembered when it happens. */
  const { error } = await supabase.auth.updateUser({
    password,
    data: { has_password: true }
  });
  if (error) throw error;
}

/** True when we know this account has a password, or has already been asked
 *  and said no. Both suppress the offer, and neither is knowable any other
 *  way — see setPassword. Absent on accounts that predate the flag, who are
 *  therefore offered once more and then recorded either way. */
export function passwordOfferSettled() {
  const m = currentUser()?.user_metadata || {};
  return m.has_password === true || m.password_offer_declined === true;
}

/** Remember a "no", so the offer is made once per account rather than after
 *  every emailed sign-in. Failure is ignored on purpose: not recording a
 *  refusal is a nuisance, and an error in front of somebody who just declined
 *  something is worse than the nuisance. */
export async function declinePasswordOffer() {
  if (!isConfigured || !currentUser()) return;
  try { await supabase.auth.updateUser({ data: { password_offer_declined: true } }); }
  catch (_) { /* offered again next time; harmless */ }
}

/**
 * Send a reset link. A public endpoint — it does not need, and does not use,
 * any admin privilege, which is exactly why the Admin screen can offer it
 * without a service-role key ever reaching a browser. The owner never handles
 * the password; the customer chooses their own.
 *
 * Deliberately does not report whether the address exists. Supabase does not
 * either, and it should not: that would turn this into a way to test which
 * emails hold accounts.
 */
export async function sendPasswordReset(email) {
  if (!isConfigured) throw new Error('Accounts need Supabase credentials in .env.local.');
  const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
    redirectTo: location.origin + location.pathname
  });
  if (error) throw error;
}

/**
 * Signs out of a real account — meaningful on a shared machine. Immediately
 * re-establishes a fresh anonymous session afterward rather than leaving the
 * app in a sessionless state: every visitor always has SOME identity, same
 * invariant initAuth() sets up at boot. That fresh session starts at zero
 * credits, same as any other new anonymous visitor.
 */
export async function signOut() {
  if (!isConfigured) return;
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
  session = null;
  const { data: anon, error: anonErr } = await supabase.auth.signInAnonymously();
  if (!anonErr) session = anon.session;
  emit('auth:changed', session?.user || null);
}

/**
 * Permanently deletes the signed-in account, through the delete-account Edge
 * Function — the service-role key that can actually remove it never reaches
 * this bundle (see services/supabase.js on where secrets go). Everything
 * else cascades away in the database: projects, their files, presets. What
 * deliberately does NOT — the credit ledger, kept for accounting; a Motion
 * licence, which was never keyed to the account at all — is explained in
 * PRIVACY.md and supabase/migrations/0029_account_deletion.sql.
 *
 * Clears the local session and opens a fresh anonymous one afterward, the
 * same tail signOut() has, rather than calling signOut() itself: the
 * account this session belonged to no longer exists server-side by the time
 * this returns, so asking Supabase to sign it out has nothing left to act on.
 */
export async function deleteAccount() {
  if (!isConfigured) throw new Error('Accounts need Supabase credentials in .env.local.');
  const { data, error } = await supabase.functions.invoke('delete-account', {});
  if (error) {
    let msg = '';
    try { msg = (await error.context?.json())?.error || ''; } catch (_) { /* not JSON */ }
    throw new Error(msg || 'Couldn’t delete the account. Is the delete-account function deployed?');
  }
  if (data && data.error) throw new Error(data.error);
  session = null;
  const { data: anon, error: anonErr } = await supabase.auth.signInAnonymously();
  if (!anonErr) session = anon.session;
  emit('auth:changed', session?.user || null);
}
